import { useState, useEffect, useCallback } from 'react';
import { paymentsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  IndianRupee, Plus, CheckCircle2, Clock, AlertTriangle, XCircle, CreditCard, Banknote,
} from 'lucide-react';

const CURRENCY_SYMBOLS = { INR: '\u20B9', AED: 'AED ', USD: '$', GBP: '\u00A3' };
const DEFAULT_RATES = { INR: 1.0, AED: 22.5, USD: 83.0, GBP: 105.0 };

const METHOD_LABELS = {
  cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer',
  card: 'Card', international_wire: 'Intl Wire', cheque: 'Cheque', bank_deposit: 'Bank Deposit',
};
const TYPE_LABELS = {
  advance: 'Advance', partial: 'Partial', balance: 'Balance', refund: 'Refund',
};

const SCHEDULE_STATUS_STYLES = {
  pending: { bg: 'rgba(245,158,11,0.08)', text: '#D97706', icon: Clock },
  paid: { bg: 'rgba(16,185,129,0.08)', text: '#059669', icon: CheckCircle2 },
  overdue: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626', icon: AlertTriangle },
  waived: { bg: 'rgba(107,114,128,0.08)', text: '#6B7280', icon: XCircle },
};

export default function OrderPayments({ orderId, orderCurrency, totalPrice, onPaymentRecorded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    amount: '', currency: orderCurrency || 'INR', exchange_rate_to_inr: DEFAULT_RATES[orderCurrency] || 1.0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'upi', reference_number: '', payment_type: 'partial', notes: '',
    // Bank deposit fields
    bank_name: '', deposit_slip_number: '', deposit_date: '',
  });

  const fetchPayments = useCallback(async () => {
    try {
      const res = await paymentsAPI.listForOrder(orderId);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch payments', err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleRecord = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    // Overpayment warning
    const paymentInr = parseFloat(form.amount) * parseFloat(form.exchange_rate_to_inr || 1);
    const currentPaid = summary.total_paid || 0;
    if (currentPaid + paymentInr > totalPrice) {
      const proceed = window.confirm(
        `This payment will exceed the order total (₹${totalPrice?.toLocaleString('en-IN')}). Total after this payment: ₹${(currentPaid + paymentInr).toLocaleString('en-IN')}. Proceed?`
      );
      if (!proceed) return;
    }
    setSubmitting(true);
    try {
      await paymentsAPI.record({
        order_id: orderId,
        amount: parseFloat(form.amount),
        currency: form.currency,
        exchange_rate_to_inr: parseFloat(form.exchange_rate_to_inr),
        payment_date: form.payment_date,
        payment_method: form.payment_method,
        reference_number: form.reference_number || null,
        payment_type: form.payment_type,
        notes: form.notes || null,
        // Bank deposit fields
        bank_name: form.payment_method === 'bank_deposit' ? form.bank_name : null,
        deposit_slip_number: form.payment_method === 'bank_deposit' ? form.deposit_slip_number : null,
        deposit_date: form.payment_method === 'bank_deposit' ? form.deposit_date : null,
      });
      toast.success('Payment recorded');
      setShowModal(false);
      setForm({
        amount: '', currency: orderCurrency || 'INR', exchange_rate_to_inr: DEFAULT_RATES[orderCurrency] || 1.0,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'upi', reference_number: '', payment_type: 'partial', notes: '',
        bank_name: '', deposit_slip_number: '', deposit_date: '',
      });
      fetchPayments();
      onPaymentRecorded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const cs = CURRENCY_SYMBOLS[orderCurrency] || '';

  if (loading) {
    return (
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardContent className="p-6 text-center">
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>Loading payments...</p>
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const schedule = data?.schedule || [];
  const payments = data?.payments || [];

  return (
    <div className="space-y-4" data-testid="order-payments-section">
      {/* Summary Bar */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Total</p>
                <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }} data-testid="payment-total">
                  {cs}{summary.total_price?.toLocaleString('en-IN')}
                </p>
              </div>
              <Separator orientation="vertical" className="h-10" />
              <div>
                <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--success)' }}>Paid</p>
                <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--success)' }} data-testid="payment-paid">
                  {cs}{summary.total_paid?.toLocaleString('en-IN')}
                </p>
              </div>
              <Separator orientation="vertical" className="h-10" />
              <div>
                <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: summary.balance_due > 0 ? 'var(--warning)' : summary.balance_due < 0 ? '#DC2626' : 'var(--ink-muted)' }}>
                  {summary.balance_due < 0 ? 'Overpaid' : 'Balance Due'}
                </p>
                <p className="text-lg font-bold tabular-nums" style={{ color: summary.balance_due > 0 ? 'var(--warning)' : summary.balance_due < 0 ? '#DC2626' : 'var(--success)' }} data-testid="payment-balance">
                  {summary.balance_due < 0 ? `−${cs}${Math.abs(summary.balance_due)?.toLocaleString('en-IN')} overpaid` : `${cs}${summary.balance_due?.toLocaleString('en-IN')}`}
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowModal(true)}
              className="gap-2 rounded-lg"
              style={{ backgroundColor: 'var(--brand-navy)' }}
              data-testid="record-payment-btn"
            >
              <Plus className="w-4 h-4" /> Record Payment
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Payment Schedule */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="payment-schedule-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Payment Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {schedule.length === 0 ? (
              <p className="text-sm py-4 text-center" style={{ color: 'var(--ink-muted)' }}>No schedule set</p>
            ) : (
              schedule.map((s) => {
                const style = SCHEDULE_STATUS_STYLES[s.status] || SCHEDULE_STATUS_STYLES.pending;
                const Icon = style.icon;
                return (
                  <div key={s.id} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ backgroundColor: style.bg }}>
                    <div className="flex items-center gap-2.5">
                      <Icon className="w-4 h-4" style={{ color: style.text }} />
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{s.milestone}</p>
                        <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                          {s.percentage}% &middot; {s.due_date || 'No due date'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums" style={{ color: style.text }}>
                        {cs}{s.amount?.toLocaleString('en-IN')}
                      </p>
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0"
                        style={{ color: style.text, borderColor: style.text + '30' }}>
                        {s.status}
                      </Badge>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Payments Received */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="payments-received-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Payments Received</CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <p className="text-sm py-4 text-center" style={{ color: 'var(--ink-muted)' }}>No payments recorded yet</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-2 px-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: p.payment_type === 'refund' ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)' }}>
                        {p.payment_type === 'refund' ? (
                          <AlertTriangle className="w-4 h-4" style={{ color: '#DC2626' }} />
                        ) : (
                          <Banknote className="w-4 h-4" style={{ color: '#059669' }} />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>
                          {CURRENCY_SYMBOLS[p.currency] || ''}{p.amount?.toLocaleString('en-IN')}
                          {p.currency !== 'INR' && (
                            <span className="text-[10px] ml-1" style={{ color: 'var(--ink-muted)' }}>
                              ({'\u20B9'}{p.amount_inr?.toLocaleString('en-IN')})
                            </span>
                          )}
                        </p>
                        <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                          {METHOD_LABELS[p.payment_method] || p.payment_method} &middot; {TYPE_LABELS[p.payment_type] || p.payment_type}
                          {p.reference_number && <> &middot; Ref: {p.reference_number}</>}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-medium" style={{ color: 'var(--ink-muted)' }}>{p.payment_date}</p>
                      <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>by {p.recorded_by_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Record Payment Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="record-payment-modal">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <Label className="text-xs">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v, exchange_rate_to_inr: DEFAULT_RATES[v] || 1.0 })}>
                  <SelectTrigger className="mt-1" data-testid="payment-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['INR', 'AED', 'USD', 'GBP'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.currency !== 'INR' && (
              <div>
                <Label className="text-xs">Exchange Rate to INR</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.exchange_rate_to_inr}
                  onChange={(e) => setForm({ ...form, exchange_rate_to_inr: e.target.value })}
                  className="mt-1"
                  data-testid="payment-exchange-rate"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payment Date *</Label>
                <Input
                  type="date"
                  value={form.payment_date}
                  onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                  className="mt-1"
                  data-testid="payment-date-input"
                />
              </div>
              <div>
                <Label className="text-xs">Method *</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="mt-1" data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(METHOD_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payment Type</Label>
                <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Reference #</Label>
                <Input
                  value={form.reference_number}
                  onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                  placeholder="Transaction ID"
                  className="mt-1"
                  data-testid="payment-ref-input"
                />
              </div>
            </div>
            
            {/* Bank Deposit Specific Fields */}
            {form.payment_method === 'bank_deposit' && (
              <div className="p-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.1)', backgroundColor: 'rgba(197,165,90,0.04)' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: 'var(--brand-navy)' }}>Bank Deposit Details</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Bank Name *</Label>
                      <Input
                        value={form.bank_name}
                        onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                        placeholder="e.g. HDFC Bank"
                        className="mt-1"
                        data-testid="bank-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Deposit Slip #</Label>
                      <Input
                        value={form.deposit_slip_number}
                        onChange={(e) => setForm({ ...form, deposit_slip_number: e.target.value })}
                        placeholder="Slip reference"
                        className="mt-1"
                        data-testid="deposit-slip-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Deposit Date (if different from payment date)</Label>
                    <Input
                      type="date"
                      value={form.deposit_date}
                      onChange={(e) => setForm({ ...form, deposit_date: e.target.value })}
                      className="mt-1"
                      data-testid="deposit-date-input"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
                className="mt-1"
                data-testid="payment-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} data-testid="payment-cancel-btn">Cancel</Button>
            <Button
              onClick={handleRecord}
              disabled={submitting}
              style={{ backgroundColor: 'var(--brand-navy)' }}
              data-testid="payment-submit-btn"
            >
              {submitting ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
